import React from "react";
import { Link } from "react-router-dom";
import LegalLayout, { LEGAL, Section, ContactLine } from "./LegalLayout";
import "./Legal.css";

const SECTIONS = [
    { id: "agreement", title: "Agreement" },
    { id: "who-may-use", title: "Who may use it" },
    { id: "accounts", title: "Your account" },
    { id: "acceptable-use", title: "Acceptable use" },
    { id: "information", title: "Information you enter" },
    { id: "availability", title: "Availability" },
    { id: "ending", title: "Suspending or ending access" },
    { id: "liability", title: "Disclaimer and liability" },
    { id: "changes", title: "Changes to these terms" },
    { id: "law", title: "Governing law" },
    { id: "contact", title: "Contact us" },
];

const TermsOfService = () => (
    <LegalLayout
        title="Terms of Service"
        sections={SECTIONS}
        intro={(
            <>
                These terms cover your use of {LEGAL.appName}, the cell reporting tool run by {LEGAL.orgName}.
                Please read them together with our <Link to="/privacy">Privacy Policy</Link>.
            </>
        )}
    >
        <Section id="agreement" title="Agreement">
            <p>By signing in to or using {LEGAL.appName}, you agree to these terms. If you do not agree, please do not use it.</p>
        </Section>

        <Section id="who-may-use" title="Who may use it">
            <p>
                {LEGAL.appName} is for cell leaders, administrators and others {LEGAL.orgName} has given an account.
                Accounts are created by church administrators; access is a privilege of serving in the cell ministry,
                not a public service.
            </p>
        </Section>

        <Section id="accounts" title="Your account">
            <ul>
                <li>Keep your sign-in details private and don&rsquo;t share your account.</li>
                <li>Use a Google or Apple account that only you control if you sign in with them.</li>
                <li>Tell an administrator straight away if you think someone else has used your account.</li>
                <li>You are responsible for what is done with your account.</li>
            </ul>
        </Section>

        <Section id="acceptable-use" title="Acceptable use">
            <p>When using {LEGAL.appName}, you agree to:</p>
            <ul>
                <li>record information accurately and only for the purposes of the cell ministry;</li>
                <li>record personal details about members and guests only where they are happy for you to do so;</li>
                <li>treat testimonies, prayer requests and other personal matters as confidential and share them only
                    as the person would expect;</li>
                <li>not try to access cells, reports or accounts your role doesn&rsquo;t allow, or interfere with how the
                    service works;</li>
                <li>not export or copy information out of {LEGAL.appName} except for legitimate ministry use, and keep any
                    exported files secure.</li>
            </ul>
        </Section>

        <Section id="information" title="Information you enter">
            <p>
                Information entered into {LEGAL.appName} is held by {LEGAL.orgName} for its ministry and handled as
                described in the <Link to="/privacy">Privacy Policy</Link>. Administrators may correct, deactivate or
                remove records, for example when someone leaves a cell.
            </p>
        </Section>

        <Section id="availability" title="Availability">
            <p>
                We try to keep {LEGAL.appName} running smoothly, but it is provided by volunteers and may sometimes be
                unavailable, change, or have errors. Features may be added, changed or removed.
            </p>
        </Section>

        <Section id="ending" title="Suspending or ending access">
            <p>
                Administrators may suspend or remove access at any time, for example when someone no longer serves
                as a leader or if these terms are not followed. You may stop using {LEGAL.appName} at any time and
                ask for your account to be deactivated.
            </p>
        </Section>

        <Section id="liability" title="Disclaimer and liability">
            <p>
                {LEGAL.appName} is provided &ldquo;as is&rdquo;, without warranties of any kind. To the extent the law
                allows, {LEGAL.orgName} is not liable for any indirect or consequential loss, or for loss of data,
                arising from use of {LEGAL.appName}. Nothing in these terms limits any rights you have that cannot
                be limited by law.
            </p>
        </Section>

        <Section id="changes" title="Changes to these terms">
            <p>
                We may update these terms. We will change the date at the top and, for significant changes, let users
                know in the app. Continuing to use {LEGAL.appName} after a change means you accept the updated terms.
            </p>
        </Section>

        <Section id="law" title="Governing law">
            <p>These terms are governed by the laws of the Province of Nova Scotia and the federal laws of Canada that apply there.</p>
        </Section>

        <Section id="contact" title="Contact us">
            <p>For questions about these terms, <ContactLine />.</p>
        </Section>
    </LegalLayout>
);

export default TermsOfService;
